import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { REMINDER_TRIGGERS, TEMPLATE_VARIABLES } from "@rentorng/core";
import { api } from "@/lib/api";
import { useApiMutation } from "@/lib/mutations";
import { useSession } from "@/lib/session";
import { Alert, Badge, Button, Card, CardTitle, ErrorState, Field, Input, PageHeader, Select, Spinner, Textarea } from "@/components/ui";

/**
 * The template editor.
 *
 * Two safety rails, both learned from what goes wrong with mail merges: variables the context
 * cannot fill are listed as they are typed, and a mistyped name (`{{tenant.frist_name}}`) is
 * called out as unknown rather than silently rendering as nothing to four hundred people.
 */

interface Template {
  id: string;
  name: string;
  channel: string;
  trigger: string | null;
  subject: string | null;
  body: string;
  isSystem: boolean;
}

export function TemplatesPage() {
  const { can } = useSession();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [draft, setDraft] = useState<{ name: string; subject: string; body: string; trigger: string; channel: string } | null>(null);

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["templates"],
    queryFn: () => api.get<{ templates: Template[] }>("/templates"),
  });

  const templates = data?.templates ?? [];
  const selected = templates.find((template) => template.id === selectedId) ?? templates[0] ?? null;

  useEffect(() => {
    if (selected && (!draft || selectedId !== selected.id)) {
      setSelectedId(selected.id);
      setDraft({
        name: selected.name,
        subject: selected.subject ?? "",
        body: selected.body,
        trigger: selected.trigger ?? "",
        channel: selected.channel,
      });
    }
  }, [selected, selectedId, draft]);

  const preview = useQuery({
    queryKey: ["template-preview", draft?.subject, draft?.body],
    queryFn: () =>
      api.post<{ subject: string | null; body: string; missing: string[]; unknown: string[]; sms: { segments: number; characters: number } }>(
        "/templates/preview",
        { subject: draft?.subject, body: draft?.body ?? "" },
      ),
    enabled: Boolean(draft?.body),
  });

  const save = useApiMutation({
    mutationFn: () => api.patch(`/templates/${selected!.id}`, draft),
    invalidates: [["templates"], ["reminder-rules"]],
  });

  const create = useApiMutation({
    mutationFn: (input: Record<string, unknown>) => api.post<{ id: string }>("/templates", input),
    invalidates: [["templates"]],
    onSuccess: (result) => setSelectedId(result.id),
  });

  if (isLoading) return <Spinner />;
  if (error) return <ErrorState error={error} retry={() => refetch()} />;

  const insert = (variable: string) => {
    setDraft((current) => (current ? { ...current, body: `${current.body}{{${variable}}}` } : current));
  };

  return (
    <div>
      <PageHeader
        title="Message templates"
        back={{ to: "/app/reminders", label: "Reminders" }}
        subtitle="What your tenants actually read. Variables in double braces are filled in for each recipient."
        actions={
          can("reminder.configure") ? (
            <Button
              variant="secondary"
              onClick={() =>
                create.mutate({
                  name: "New template",
                  channel: "email",
                  subject: "About your tenancy at {{unit.label}}",
                  body: "Dear {{tenant.first_name}},\n\n\n\nRegards,\n{{agency.name}}",
                })
              }
            >
              New template
            </Button>
          ) : null
        }
      />

      <div className="grid gap-5 lg:grid-cols-4">
        <Card padded={false} className="lg:col-span-1">
          <ul className="divide-y divide-ink-100">
            {templates.map((template) => (
              <li key={template.id}>
                <button
                  type="button"
                  onClick={() => {
                    setSelectedId(template.id);
                    setDraft({
                      name: template.name,
                      subject: template.subject ?? "",
                      body: template.body,
                      trigger: template.trigger ?? "",
                      channel: template.channel,
                    });
                  }}
                  className={`block w-full px-4 py-3 text-left text-sm hover:bg-ink-50 ${
                    template.id === selected?.id ? "bg-brand-50 font-medium text-brand-800" : ""
                  }`}
                >
                  {template.name}
                  <span className="mt-0.5 block text-xs text-ink-400">
                    {template.channel}
                    {template.isSystem ? " · built in" : ""}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </Card>

        <div className="space-y-5 lg:col-span-3">
          {draft && selected ? (
            <>
              <Card>
                <CardTitle
                  hint={selected.isSystem ? "A built-in template. You can edit the wording; it cannot be deleted." : undefined}
                  action={
                    can("reminder.configure") ? (
                      <Button size="sm" disabled={save.isPending} onClick={() => save.mutate(undefined as never)}>
                        {save.isPending ? "Saving…" : "Save"}
                      </Button>
                    ) : null
                  }
                >
                  {selected.name}
                </CardTitle>

                {save.errorMessage ? <Alert tone="danger">{save.errorMessage}</Alert> : null}
                {save.isSuccess ? <Alert tone="success">Saved.</Alert> : null}

                <div className="mt-3 space-y-4">
                  <div className="grid gap-4 sm:grid-cols-3">
                    <Field label="Name" htmlFor="name">
                      <Input id="name" placeholder="Rent due in seven days" value={draft.name} onChange={(event) => setDraft({ ...draft, name: event.target.value })} />
                    </Field>
                    <Field label="Channel" htmlFor="channel">
                      <Select id="channel" value={draft.channel} onChange={(event) => setDraft({ ...draft, channel: event.target.value })}>
                        <option value="email">Email</option>
                        <option value="sms">SMS</option>
                        <option value="whatsapp">WhatsApp</option>
                      </Select>
                    </Field>
                    <Field label="For" htmlFor="trigger" hint="Rules default to the template matching their trigger.">
                      <Select id="trigger" value={draft.trigger} onChange={(event) => setDraft({ ...draft, trigger: event.target.value })}>
                        <option value="">Anything</option>
                        {REMINDER_TRIGGERS.map((trigger) => (
                          <option key={trigger.value} value={trigger.value}>
                            {trigger.label}
                          </option>
                        ))}
                      </Select>
                    </Field>
                  </div>

                  <Field label="Subject" htmlFor="subject">
                    <Input id="subject" placeholder="Your rent falls due on {due_date}" value={draft.subject} onChange={(event) => setDraft({ ...draft, subject: event.target.value })} />
                  </Field>

                  <Field label="Message" htmlFor="body" required>
                    <Textarea id="body" rows={12} placeholder="Hello {tenant_name}, your rent of {amount} falls due on {due_date}." value={draft.body} onChange={(event) => setDraft({ ...draft, body: event.target.value })} />
                  </Field>

                  {preview.data?.unknown.length ? (
                    <Alert tone="danger" title="Unknown variables">
                      {preview.data.unknown.join(", ")} — check the spelling against the list below, or they will render as nothing.
                    </Alert>
                  ) : null}

                  {draft.channel !== "email" && preview.data?.sms ? (
                    <p className="text-xs text-ink-500">
                      {preview.data.sms.characters} characters · {preview.data.sms.segments} SMS{" "}
                      {preview.data.sms.segments === 1 ? "segment" : "segments"} per recipient — each segment is billed.
                    </p>
                  ) : null}
                </div>
              </Card>

              <Card>
                <CardTitle hint="Resolved against sample data.">Preview</CardTitle>
                {preview.isLoading ? (
                  <Spinner />
                ) : preview.data ? (
                  <div className="rounded-lg border border-ink-200 bg-ink-50 p-4">
                    {preview.data.subject ? <p className="text-sm font-medium">{preview.data.subject}</p> : null}
                    <p className="mt-2 whitespace-pre-wrap text-sm text-ink-700">{preview.data.body}</p>
                  </div>
                ) : null}
              </Card>

              <Card>
                <CardTitle hint="Click one to add it at the end of the message.">Variables you can use</CardTitle>
                <div className="flex flex-wrap gap-1.5">
                  {TEMPLATE_VARIABLES.map((variable) => (
                    <button
                      key={variable.name}
                      type="button"
                      onClick={() => insert(variable.name)}
                      title={`${variable.description} — e.g. ${variable.example}`}
                      className="rounded-full border border-ink-200 bg-white px-2 py-0.5 font-mono text-xs text-ink-600 hover:border-brand-600 hover:text-brand-700"
                    >
                      {variable.name}
                    </button>
                  ))}
                </div>
                {preview.data?.missing.length ? (
                  <p className="mt-3 text-xs text-ink-500">
                    Not filled by the sample data: <Badge tone="muted">{preview.data.missing.join(", ")}</Badge>
                  </p>
                ) : null}
              </Card>
            </>
          ) : null}
        </div>
      </div>
    </div>
  );
}
